/**
 * Genera le declinazioni di un colore primario per Tailwind CSS
 * Crea una palette completa da 50 (più chiaro) a 900 (più scuro)
 */
export function generateColorPalette(primaryColor: string) {
  // Converte hex in RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Converte RGB in hex
  const rgbToHex = (r: number, g: number, b: number) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  // Genera tonalità più chiare
  const lighten = (r: number, g: number, b: number, factor: number) => {
    return {
      r: Math.round(r + (255 - r) * factor),
      g: Math.round(g + (255 - g) * factor),
      b: Math.round(b + (255 - b) * factor)
    };
  };

  // Genera tonalità più scure
  const darken = (r: number, g: number, b: number, factor: number) => {
    return {
      r: Math.round(r * (1 - factor)),
      g: Math.round(g * (1 - factor)),
      b: Math.round(b * (1 - factor))
    };
  };

  const rgb = hexToRgb(primaryColor);
  if (!rgb) return {};

  const palette: Record<string, string> = {};

  // Genera le tonalità chiare (50-400)
  const lightFactors = [0.95, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2];
  lightFactors.forEach((factor, index) => {
    const lightened = lighten(rgb.r, rgb.g, rgb.b, factor);
    const shade = (index + 1) * 50;
    palette[shade] = rgbToHex(lightened.r, lightened.g, lightened.b);
  });

  // Colore principale (500)
  palette[500] = primaryColor;

  // Genera le tonalità scure (600-900)
  const darkFactors = [0.2, 0.3, 0.4, 0.5, 0.6];
  darkFactors.forEach((factor, index) => {
    const darkened = darken(rgb.r, rgb.g, rgb.b, factor);
    const shade = 600 + (index * 100);
    palette[shade] = rgbToHex(darkened.r, darkened.g, darkened.b);
  });

  return palette;
}

/**
 * Aggiorna dinamicamente le variabili CSS per i colori Tailwind
 */
export function updateTailwindColors(primaryColor: string) {
  const palette = generateColorPalette(primaryColor);
  
  // Aggiorna le variabili CSS root
  const root = document.documentElement;
  
  Object.entries(palette).forEach(([shade, color]) => {
    root.style.setProperty(`--color-shblue-${shade}`, color);
    
    // Aggiungi anche la versione RGB per le ombre
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    const rgb = hexToRgb(color);
    if (rgb) {
      root.style.setProperty(`--color-shblue-${shade}-rgb`, `${rgb.r},${rgb.g},${rgb.b}`);
    }
  });
}

/**
 * Applica i colori dinamicamente agli elementi esistenti
 */
export function applyDynamicColors(primaryColor: string) {
  updateTailwindColors(primaryColor);
  
  // Forza il re-render dei componenti che usano i colori
  const event = new CustomEvent('colorsUpdated', { 
    detail: { primaryColor } 
  });
  window.dispatchEvent(event);
}
